import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { User } from '@/types';
import { hasPermission as checkPermission } from '@/constants/auth';

/**
 * Multi-factory: the sentinel id used by the backend to mean "all factories"
 * (boss / HQ cross-factory view). Mirrors backend factoryScope.ts.
 */
export const ALL_FACTORIES_SENTINEL = 0;

export interface FactoryOption {
  id: number;
  code: string;
  name: string;
  short_name: string | null;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;

  // Multi-factory state, loaded on login + after /auth/switch-factory.
  currentFactoryId: number | null;
  primaryFactoryId: number | null;
  factories: FactoryOption[];
  hasAllFactoriesAccess: boolean;

  login: (token: string, user: User) => void;
  logout: () => void;
  setFactoryState: (state: {
    factories: FactoryOption[];
    currentFactoryId: number;
    primaryFactoryId: number;
    hasAllFactoriesAccess: boolean;
  }) => void;
  setToken: (token: string) => void;
  setCurrentFactoryId: (id: number) => void;
  hasPermission: (permission: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,

      currentFactoryId: null,
      primaryFactoryId: null,
      factories: [],
      hasAllFactoriesAccess: false,

      login: (token, user) => {
        set({ token, user, isAuthenticated: true });
      },

      logout: () => {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          currentFactoryId: null,
          primaryFactoryId: null,
          factories: [],
          hasAllFactoriesAccess: false,
        });
      },

      setFactoryState: ({ factories, currentFactoryId, primaryFactoryId, hasAllFactoriesAccess }) => {
        set({ factories, currentFactoryId, primaryFactoryId, hasAllFactoriesAccess });
      },

      setToken: (token) => set({ token }),

      setCurrentFactoryId: (id) => set({ currentFactoryId: id }),

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        return checkPermission(user, permission);
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: Boolean(state.token && state.user),
        currentFactoryId: state.currentFactoryId,
        primaryFactoryId: state.primaryFactoryId,
        factories: state.factories,
        hasAllFactoriesAccess: state.hasAllFactoriesAccess,
      }),
    }
  )
);
